import { useEffect, useState } from 'react'
import { supabase, type PublishedJob } from '../lib/supabase'
import { useToast } from './ui/use-toast'
import { MapPin, Globe, Train, Briefcase, Download } from 'lucide-react'

function DecimalsDesign() {
  const [jobs, setJobs] = useState<PublishedJob[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set())
  const { toast } = useToast()

  useEffect(() => {
    fetchJobs()
  }, [])

  async function fetchJobs() {
    try {
      setLoading(true)

      // Fetch all design jobs first
      const { data: allDesignJobs, error: jobsError } = await supabase
        .from('published_jobs')
        .select('*')
        .eq('job_family', 'Design')
        .order('job_posted_at', { ascending: false })

      if (jobsError) {
        console.error('Error fetching jobs:', jobsError)
        throw jobsError
      }

      // Filter for senior+ roles
      const filteredJobs = allDesignJobs?.filter(job => {
        if (!job.seniority) return false
        return job.seniority === 'Senior' || job.seniority === 'Lead' || job.seniority === 'Principal'
      }) || []

      setJobs(filteredJobs)
      setTotalCount(filteredJobs.length)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch jobs')
      console.error('Error fetching jobs:', err)
    } finally {
      setLoading(false)
    }
  }

  function getCompanyInitials(companyName: string): string {
    const words = companyName.trim().split(/\s+/)
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase()
    }
    return (words[0][0] + words[1][0]).toUpperCase()
  }

  function formatDate(dateString: string | null): string {
    if (!dateString) return '—'
    const date = new Date(dateString)
    const now = new Date()
    const diffInMs = now.getTime() - date.getTime()
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))

    if (diffInDays === 0) return 'Today'
    if (diffInDays === 1) return 'Yesterday'
    if (diffInDays < 7) return `${diffInDays}d ago`
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)}w ago`

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  function formatLocation(job: PublishedJob): string {
    // For remote jobs, show remote countries
    if (job.remote_countries && job.remote_countries.length > 0) {
      if (job.remote_countries.length === 1) return job.remote_countries[0]
      return 'Multiple'
    }

    // For onsite/hybrid, show country
    if (job.country && job.country.length > 0) {
      if (job.country.length === 1) return job.country[0]
      return 'Multiple'
    }

    return '—'
  }

  function formatFundingStage(stage: string | null): string {
    if (!stage || stage.toLowerCase() === 'unknown') return '—'

    // Handle series rounds: series-a, series-b, series-c, etc.
    if (stage.toLowerCase().startsWith('series')) {
      const parts = stage.split('-')
      if (parts.length === 2) {
        return `Series ${parts[1].toUpperCase()}`
      }
    }

    // Capitalize first letter for other stages
    return stage.charAt(0).toUpperCase() + stage.slice(1).toLowerCase()
  }

  function formatSalary(min: number | null, max: number | null, currency: string | null): string {
    if (!min && !max) return '—'

    const currencySymbol = currency === 'USD' || !currency ? '$' : currency

    // Convert to thousands and format compactly
    if (min && max) {
      const minK = Math.round(min / 1000)
      const maxK = Math.round(max / 1000)
      return `${currencySymbol}${minK}-${maxK}k`
    }

    if (min) {
      const minK = Math.round(min / 1000)
      return `${currencySymbol}${minK}k+`
    }

    const maxK = Math.round(max! / 1000)
    return `Up to ${currencySymbol}${maxK}k`
  }

  function handleJobClick(job: PublishedJob) {
    // Open job page in new tab
    if (job.job_url) {
      window.open(job.job_url, '_blank')
    }
  }

  function handleCompanyClick(job: PublishedJob, e: React.MouseEvent) {
    e.stopPropagation()
    // Open company website in new tab
    if (job.website_url) {
      window.open(job.website_url, '_blank')
    }
  }

  function handleImageError(jobId: string) {
    setFailedImages(prev => new Set(prev).add(jobId))
  }

  function exportToCSV() {
    // Prepare CSV headers
    const headers = ['Company', 'Job Title', 'Location', 'Funding Stage', 'Salary', 'Posted', 'Job URL', 'Website URL']

    // Prepare CSV rows
    const rows = jobs.map(job => {
      return [
        job.company_name_full,
        job.job_title,
        formatLocation(job),
        formatFundingStage(job.funding_stage),
        formatSalary(job.salary_min, job.salary_max, job.salary_currency),
        formatDate(job.job_posted_at || job.created_at),
        job.job_url || '',
        job.website_url || ''
      ].map(field => {
        // Escape quotes and wrap in quotes if contains comma or quote
        const stringField = String(field)
        if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
          return `"${stringField.replace(/"/g, '""')}"`
        }
        return stringField
      })
    })

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n')

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)

    const today = new Date().toISOString().split('T')[0]
    link.setAttribute('href', url)
    link.setAttribute('download', `senior-design-jobs-${today}.csv`)
    link.style.visibility = 'hidden'

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    toast({
      description: `Exported ${jobs.length} ${jobs.length === 1 ? 'job' : 'jobs'} to CSV`,
    })
  }

  if (loading) {
    return (
      <div className="bg-white pb-8">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="mb-8">
            <div className="h-10 w-64 bg-gray-200 rounded animate-pulse mb-2"></div>
            <div className="h-6 w-96 bg-gray-200 rounded animate-pulse"></div>
          </div>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="h-96 bg-gray-100 animate-pulse"></div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white pb-8">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
            Error: {error}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white pb-8">
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between gap-4 mb-3">
            <h1 className="text-4xl font-bold text-gray-900">
              Senior Design Roles
            </h1>
            <button
              onClick={exportToCSV}
              disabled={jobs.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Export to CSV"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
          <p className="text-lg text-gray-600">
            {totalCount.toLocaleString()} senior+ design {totalCount === 1 ? 'job' : 'jobs'}. Updated hourly.
          </p>
        </div>

        {/* Jobs Table */}
        {jobs.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center text-gray-600">
            No senior design roles available yet. Check back soon!
          </div>
        ) : (
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Company
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Job
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Stage
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Salary
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      Posted
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {jobs.map((job) => (
                    <tr
                      key={job.id}
                      className="hover:bg-blue-50 transition-colors"
                    >
                      {/* Company Name with Logo */}
                      <td className="px-4 py-3 align-middle">
                        <div
                          className="flex items-center gap-2 cursor-pointer"
                          onClick={(e) => handleCompanyClick(job, e)}
                        >
                          {job.logo_url && !failedImages.has(job.id) ? (
                            <img
                              src={job.logo_url}
                              alt={job.company_name_full}
                              className="w-7 h-7 rounded-lg object-contain bg-white shrink-0"
                              onError={() => handleImageError(job.id)}
                            />
                          ) : (
                            <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-500 text-white text-xs font-semibold shrink-0">
                              {getCompanyInitials(job.company_name_full)}
                            </div>
                          )}
                          <span className="text-sm font-medium text-gray-900 whitespace-nowrap">
                            {job.company_name_full}
                          </span>
                        </div>
                      </td>

                      {/* Job Title */}
                      <td
                        className="px-4 py-3 align-middle cursor-pointer max-w-[200px]"
                        onClick={() => handleJobClick(job)}
                      >
                        <div className="text-sm text-gray-900 hover:text-blue-600 transition-colors truncate">
                          {job.job_title}
                        </div>
                      </td>

                      {/* Location */}
                      <td className="px-4 py-3 text-sm text-gray-600 align-middle">
                        <div className="flex items-center gap-1">
                          {job.work_setting === 'remote' && <Globe className="w-3 h-3 shrink-0 text-blue-500" />}
                          {job.work_setting === 'hybrid' && <Train className="w-3 h-3 shrink-0 text-purple-500" />}
                          {job.work_setting === 'on-site' && <Briefcase className="w-3 h-3 shrink-0 text-gray-500" />}
                          {!job.work_setting && <MapPin className="w-3 h-3 shrink-0 text-gray-400" />}
                          <span className="whitespace-normal leading-relaxed">
                            {formatLocation(job)}
                          </span>
                        </div>
                      </td>

                      {/* Funding Stage */}
                      <td className="px-4 py-3 text-sm text-gray-600 align-middle whitespace-nowrap">
                        {formatFundingStage(job.funding_stage)}
                      </td>

                      {/* Salary */}
                      <td className="px-4 py-3 text-sm text-gray-600 align-middle whitespace-nowrap">
                        {formatSalary(job.salary_min, job.salary_max, job.salary_currency)}
                      </td>

                      {/* Posted Date */}
                      <td className="px-4 py-3 text-sm text-gray-500 align-middle whitespace-nowrap">
                        {formatDate(job.job_posted_at || job.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default DecimalsDesign
